import { Typography, Tabs, message } from "antd";
import React, { useContext, useEffect, useRef, useState } from "react";
import mockTripData from "./mockTripData";
import Geofencing from "./GeoFencing";
import LiveTracking from "./LiveTracking";
import Trip from "./Trip";
import LocateMyVehicle from "./LocateMyVehicle";
import {
  sendTripData as sendTripDataToServer,
  sendLiveLocationDataToMqttTopic,
} from "../../mqtt/location.js";
import { DispatchContext, SimulatorContext } from "store/context";
import { v4 as uuidv4 } from "uuid";
import { uploadTripFileToS3 } from "mqtt/aws";
import RemoteFunctions from "components/RemoteFunctions";

const { Title } = Typography;

const predefinedPlots = mockTripData;

const LocationBasedServices = () => {
  const dispatch = useContext(DispatchContext);
  const {
    vehicleSimulatorSettings,
    stsToken: credentials,
    liveTrackingSession,
  } = useContext(SimulatorContext);
  const vehicle = vehicleSimulatorSettings; // { serialNo: SERIALNO, vehicleKey: VEHICLEKEY }; // Example serial number, replace with actual value
  const [selectedPlot, setSelectedPlot] = useState(mockTripData[0]);
  const [customPlotter, setCustomPlotter] = useState(false);
  const [plots, setPlots] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();
  const [loadingSendingTripData, setLoadingSendingTripData] = useState(false);
  const [loadingUploadingFile, setLoadingUploadingFile] = useState(false);
  const [
    intermediateCoordinatesForSelectedRoute,
    setIntermediateCoordinatesForSelectedRoute,
  ] = useState([]);
  const [vehiclePositionInParent, setVehiclePositionInParent] = useState(null);
  const [previousVehiclePositionInParent, setPreviousVehiclePositionInParent] =
    useState(null);
  const lastSendTsRef = useRef(0);
  const pendingSendRef = useRef(null);
  const latestPosRef = useRef(null);

  const {
    sessionId: liveTrackingSessionId,
    // @ts-ignore
    action: liveTrackingSessionAction,
  } = liveTrackingSession || {};

  const [previousLiveTrackingSessionId, setPreviousLiveTrackingSessionId] =
    useState(null); // For storing previous live tracking session ID

  const handleSelectPlot = (plot) => {
    setSelectedPlot(plot);
  };

  const processTripPayload = (trip) => {
    // If already in processed format, return as is
    if (trip.type === "trip_analysis" && trip.data) return trip;

    // Convert custom/simple trip to processed payload structure
    const formatTimestampWithTZ = (t) => {
      if (t === undefined || t === null || t === "") return "";
      const d = new Date(t);
      return isNaN(d.getTime()) ? "" : d.toISOString(); // ISO 8601 with timezone (UTC "Z")
    };

    return {
      type: "trip_analysis",
      id: uuidv4(),
      serialNo: trip.serialNo || (vehicle && vehicle.serialNo) || "",
      data: {
        start: {
          time: formatTimestampWithTZ(trip.startLocation?.time),
          location: {
            longitude: trip.startLocation?.coordinates?.[0] ?? null,
            latitude: trip.startLocation?.coordinates?.[1] ?? null,
          },
        },
        end: {
          time: formatTimestampWithTZ(trip.endLocation?.time),
          location: {
            longitude: trip.endLocation?.coordinates?.[0] ?? null,
            latitude: trip.endLocation?.coordinates?.[1] ?? null,
          },
        },
        places: (trip.places || []).map((p) => ({
          placeId: p.placeId,
          time: formatTimestampWithTZ(p.time),
          location: {
            longitude: p.location?.coordinates?.[0] ?? null,
            latitude: p.location?.coordinates?.[1] ?? null,
          },
          coordinates: p.location?.coordinates ?? [],
        })),
        distance: trip.distance || 0,
        drivingDetails: trip.drivingDetails || {}
      },
    };
  };

  const sendTripData = async (trip = mockTripData[1]) => {
    setLoadingSendingTripData(true);
    const processedTrip = processTripPayload(trip);

    // Send trip data to the backend using MQTT
    try {
      // await sendTripDataToServer(plot);
      const payload = { ...processedTrip, serialNo: vehicle?.serialNo };
      await sendTripDataToServer(payload, credentials);
      console.log("Trip data sent to server");
      if (intermediateCoordinatesForSelectedRoute?.length > 0) {
        // If there are intermediate coordinates, upload them to S3
        messageApi.warning(
          "Started uploading intermediate coordinates to S3..."
        );
        const geometryFile = new Blob(
          [JSON.stringify(intermediateCoordinatesForSelectedRoute)],
          { type: "application/json" }
        );
        await uploadGeometryFileToS3(geometryFile, processedTrip);
      } else {
        messageApi.warning("No intermediate coordinates to upload");
      }

      messageApi.success("Trip data published to MQTT topic successfully");
    } catch (e) {
      console.error("Failed to send trip data to server", e);
      messageApi.error("Failed to publish trip data");
    } finally {
      setLoadingSendingTripData(false);
    }
  };

  const uploadGeometryFileToS3 = async (file, processedTrip) => {
    setLoadingUploadingFile(true);
    // const bucketName =
    //   "ota-next-dev-test-trip-intermediate-coordinates-geometry"; // Replace with your S3 bucket name
    const key = `locationBasedServicesObjects/${vehicle?.serialNo}/trips/${processedTrip?.id}/places-geometry-${processedTrip?.id}.json`; // Use a unique key for the file, e.g., by using the file name
    try {
      const uploadResponse = await uploadTripFileToS3(file, key, credentials);
      console.log("File uploaded to S3 bucket");
      messageApi.success("File uploaded to S3 bucket successfully");
    } catch (e) {
      console.error("Failed to upload file to S3 bucket", e);
      messageApi.error("Failed to upload file to S3 bucket");
    } finally {
      setLoadingUploadingFile(false);
    }
  };

  const onChange = (key) => {
    console.log(key);
  };

  const tripProps = {
    loadingUploadingFile,
    setIntermediateCoordinatesForSelectedRoute,
    loadingSendingTripData,
    messageApi,
    vehicle,
    predefinedPlots,
    customPlotter,
    setCustomPlotter,
    plots,
    sendTripData,
    selectedPlot,
    setSelectedPlot,
    setPlots,
    handleSelectPlot,
  };

  const onLiveTrackingShadowUpdate = (liveTracking) => {
    // Handle live tracking shadow update
    console.log("Live tracking shadow update received:", liveTracking);
    dispatch({
      type: "UPDATE_LIVE_TRACKING",
      payload: liveTracking,
    });
  };

  // Simulate sending location to server
  const sendLocationToServer = React.useCallback(
    (vehiclePos) => {
      if (vehiclePos) {
        console.log(
          "Sending live tracking Session update - location update to mqtt : ",
          vehiclePos
        );
        const coords = vehiclePos; // Use current vehicle position
        if (!vehicle?.serialNo || !vehicle?.vehicleKey) {
          console.error("Missing vehicle identifiers");
          return;
        }

        const locationUpdatePayload = {
          type: "live_tracking",
          subType: "session_update", // session_start, session_update, session_end
          id: liveTrackingSessionId || uuidv4(), // Generate a unique ID for each update
          serialNo: vehicle?.serialNo,
          vehicleKey: vehicle?.vehicleKey,
          time: new Date().getTime(),
          location: {
            longitude: coords[1], // 80.19582698476104,
            latitude: coords[0], // 12.967090247180218,
          },
        };
        setPreviousLiveTrackingSessionId(liveTrackingSessionId);
        sendLiveLocationDataToMqttTopic(locationUpdatePayload, credentials);
      }
    },
    [vehicle, liveTrackingSessionId, credentials]
  );

  const stopSendingLocationToServer = (vehiclePos) => {
    const lastLoc = vehiclePos || vehiclePositionInParent;
    if (lastLoc) {
      if (previousLiveTrackingSessionId !== null) {
        console.log(
          "Ending live tracking session - final live tracking update to mqtt : ",
          vehiclePos
        );
        const coords = lastLoc; // Use current vehicle position
        if (!vehicle?.serialNo || !vehicle?.vehicleKey) {
          console.error("Missing vehicle identifiers");
          return;
        }
        // Replace with actual API call if needed
        // console.log("Session end - vehicle location update to server:", coords);
        const locationUpdatePayload = {
          type: "live_tracking",
          subType: "session_end", // session_start, session_update, session_end
          id: previousLiveTrackingSessionId || uuidv4(), // Generate a unique ID for each update
          serialNo: vehicle?.serialNo,
          vehicleKey: vehicle?.vehicleKey,
          time: new Date().getTime(),
          location: {
            longitude: coords[1], // 12.967090247180218,
            latitude: coords[0], // 80.19582698476104,
          },
        };
        setPreviousLiveTrackingSessionId(null); // Reset previous session ID
        sendLiveLocationDataToMqttTopic(locationUpdatePayload, credentials);
      }
    }
  };

  // keep the latest position in a ref so scheduled sends use the freshest value
  useEffect(() => {
    latestPosRef.current = vehiclePositionInParent;
  }, [vehiclePositionInParent]);

  // Throttled sending: at most once every 5000ms
  useEffect(() => {
    if (!vehiclePositionInParent) return;

    if (pendingSendRef.current) {
      clearTimeout(pendingSendRef.current);
      pendingSendRef.current = null;
    }

    const now = Date.now();
    const elapsed = now - lastSendTsRef.current;
    const wait = elapsed >= 5000 ? 0 : 5000 - elapsed;

    pendingSendRef.current = setTimeout(() => {
      const pos = latestPosRef.current;
      if (pos) {
        sendLocationToServer(pos);
        setPreviousVehiclePositionInParent(pos);
        lastSendTsRef.current = Date.now();
      }
      pendingSendRef.current = null;
    }, wait);

    return () => {
      if (pendingSendRef.current) {
        clearTimeout(pendingSendRef.current);
        pendingSendRef.current = null;
      }
    };
  }, [vehiclePositionInParent, sendLocationToServer]);

  const items = [
    {
      key: "1",
      label: "Trip",
      children: (
        <>
          <Trip {...tripProps} />
        </>
      ),
    },
    {
      key: "2",
      label: "Geofencing",
      children: <Geofencing vehicle={vehicle} />,
    },
    {
      key: "3",
      label: "Live Tracking",
      children: (
        <LiveTracking
          liveTrackingSessionState={liveTrackingSession}
          plots={predefinedPlots}
          // onChange={onChange}
          vehicle={vehicle}
          sendLocationToServer={sendLocationToServer}
          stopSendingLocationToServer={stopSendingLocationToServer}
          setVehiclePositionInParent={setVehiclePositionInParent}
        />
      ),
    },
    {
      key: "4",
      label: "Locate My Vehicle",
      children: <LocateMyVehicle />,
    },
  ];

  const portalControls = useRef(null);

  return (
    <>
      {contextHolder}
      <Typography>
        <Title level={5} style={{ margin: 0, marginBottom: "10px" }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            Location Based Services
            <div
              ref={portalControls}
              id="remote-functions-control-portal"
            ></div>
          </div>
        </Title>

        <Tabs defaultActiveKey="1" items={items} onChange={onChange} />
      </Typography>
      {portalControls && portalControls.current && (
        <div style={{ display: "none" }}>
          <RemoteFunctions
            onLiveTrackingShadowUpdate={onLiveTrackingShadowUpdate}
            portalControls={portalControls}
          />
        </div>
      )}
    </>
  );
};
export default LocationBasedServices;
